/* AutomationsList — /admin/automations
   Table of automations with create / toggle / delete actions */
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import css from '../../AdminPage.module.css';
import {
  useAutomations,
  useCreateAutomation,
  useDeleteAutomation,
  useToggleAutomation,
  useDuplicateAutomation,
} from '../../../../hooks/useAutomations';
import { useToast } from '../../../../hooks/useToast';
import Badge from '../../../../components/ui/Badge/Badge';
import EmptyState from '../../../../components/ui/EmptyState/EmptyState';
import SearchInput from '../../../../components/ui/SearchInput/SearchInput';

export default function AutomationsList() {
  const { data: automations = [], isLoading } = useAutomations();
  const createAutomation = useCreateAutomation();
  const deleteAutomation = useDeleteAutomation();
  const toggleAutomation = useToggleAutomation();
  const duplicateAutomation = useDuplicateAutomation();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const ROWS_PER_PAGE = 10;
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    if (!search) return automations;
    const q = search.toLowerCase();
    return automations.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        (a.case_type_name || '').toLowerCase().includes(q) ||
        (a.description || '').toLowerCase().includes(q),
    );
  }, [automations, search]);

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const paged = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  async function handleCreate() {
    try {
      const result = await createAutomation.mutateAsync({});
      navigate(`/admin/automations/${result.id}`);
    } catch {
      showToast('Failed to create automation', 'error');
    }
  }

  async function handleToggle(e: React.MouseEvent, id: number) {
    e.stopPropagation();
    try {
      const result = await toggleAutomation.mutateAsync(id);
      showToast(`Automation ${result.is_active ? 'activated' : 'deactivated'}`, 'success');
    } catch {
      showToast('Failed to toggle automation', 'error');
    }
  }

  async function handleDelete(e: React.MouseEvent, id: number, name: string) {
    e.stopPropagation();
    if (!confirm(`Delete automation "${name}"? This cannot be undone.`)) return;
    try {
      await deleteAutomation.mutateAsync(id);
      showToast('Automation deleted', 'success');
    } catch {
      showToast('Failed to delete automation', 'error');
    }
  }

  async function handleDuplicate(e: React.MouseEvent, id: number, name: string) {
    e.stopPropagation();
    try {
      const result = await duplicateAutomation.mutateAsync(id);
      showToast(`Duplicated "${name}"`, 'success');
      navigate(`/admin/automations/${result.id}`);
    } catch {
      showToast('Failed to duplicate automation', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <div className={css.section}>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>⚡ Automations</h2>
        <div className={css.toolbar}>
          <SearchInput
            placeholder="Search automations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            className={css.btnPrimary}
            onClick={handleCreate}
            disabled={createAutomation.isPending}
          >
            + Create New
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon="⚡"
          message={search ? 'No automations match your search' : 'No automations yet'}
        >
          {!search && (
            <button
              className={css.btnPrimary}
              onClick={handleCreate}
              disabled={createAutomation.isPending}
            >
              Create your first automation
            </button>
          )}
        </EmptyState>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Pipeline</th>
                  <th>Nodes</th>
                  <th>Active</th>
                  <th>Last Run</th>
                  <th>Created</th>
                  <th style={{ width: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((a) => (
                  <tr
                    key={a.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/automations/${a.id}`)}
                  >
                    <td style={{ fontWeight: 600 }}>{a.name}</td>
                    <td>{a.case_type_name || '—'}</td>
                    <td>{a.node_count ?? 0}</td>
                    <td>
                      <Badge
                        variant={a.is_active ? 'success' : 'warning'}
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => handleToggle(e, a.id)}
                      >
                        {a.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td style={{ fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}>
                      {a.last_run_at ? new Date(a.last_run_at).toLocaleString() : '—'}
                    </td>
                    <td style={{ fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}>
                      {new Date(a.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          className={css.btnSecondary}
                          style={{ fontSize: 'var(--font-size-xs)', padding: '2px 8px' }}
                          title="Duplicate automation"
                          onClick={(e) => handleDuplicate(e, a.id, a.name)}
                          disabled={duplicateAutomation.isPending}
                        >
                          Copy
                        </button>
                        <button
                          className={css.btnDanger}
                          style={{ fontSize: 'var(--font-size-xs)', padding: '2px 8px' }}
                          onClick={(e) => handleDelete(e, a.id, a.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className={css.pagination}>
              <button
                className={css.pageBtn}
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                ‹ Prev
              </button>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-500)' }}>
                Page {page} of {totalPages} ({filtered.length} total)
              </span>
              <button
                className={css.pageBtn}
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next ›
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
